import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";
import { Save, X, RotateCcw, Trash2, Plus, Check, MousePointer2 } from "lucide-react";
import { toast } from "sonner";

/*  Polygon-based Hotspot Editor
    - Modes:
        • select   → click a polygon to select; drag its vertices to reshape; drag polygon body to move.
        • draw     → click on the plan to place vertices; press Enter or double-click to close.
    - Coordinate system: percentages 0..100 relative to displayed floor plan.
*/

const UNIT_COLORS = {
  base:     "rgba(156,124,84,0.35)",
  baseLn:   "#9C7C54",
  sel:      "rgba(37,99,235,0.45)",
  selLn:    "#2563EB",
  draft:    "rgba(16,185,129,0.35)",
  draftLn:  "#059669",
};

export default function HotspotEditor({ floor, wing, floorPlanUrl, onClose }) {
  const { user } = useAuth();
  const [hotspots, setHotspots] = useState([]);
  const [original, setOriginal] = useState([]);
  const [selected, setSelected] = useState(null);   // unit_id
  const [mode, setMode]         = useState("select"); // "select" | "draw"
  const [draftUnit, setDraftUnit] = useState("");
  const [draftPoints, setDraftPoints] = useState([]); // [[x,y] ...] percentages
  const [drag, setDrag]         = useState(null);   // { type, unit_id, vertexIndex?, startX, startY, origPoints }
  const svgRef                  = useRef(null);
  const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : {};

  useEffect(() => {
    const params = new URLSearchParams({ floor });
    if (wing !== undefined) params.append("wing", wing);
    axios.get(`${API}/hotspots?${params}`)
      .then((r) => {
        setHotspots(r.data);
        setOriginal(JSON.parse(JSON.stringify(r.data)));
      })
      .catch(() => setHotspots([]));
  }, [floor, wing]);

  const getSvgPoint = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top)  / rect.height) * 100;
    return [Math.max(0, Math.min(100, x)), Math.max(0, Math.min(100, y))];
  };

  /* ─── Draw mode ─── */
  const handlePlanClick = (e) => {
    if (mode !== "draw") return;
    // Clicks on existing polygons bubble up; ignore if target is a vertex handle
    if (e.target && e.target.dataset && e.target.dataset.vertex) return;
    const p = getSvgPoint(e);
    setDraftPoints((prev) => [...prev, p]);
  };

  const commitDraft = () => {
    if (draftPoints.length < 3) {
      toast.error("A polygon needs at least 3 points");
      return;
    }
    const uid = (draftUnit || "").trim();
    if (!uid) {
      toast.error("Enter a Unit ID for this polygon");
      return;
    }
    if (hotspots.find((h) => h.unit_id === uid)) {
      toast.error(`Hotspot for "${uid}" already exists — delete it first or select it to edit`);
      return;
    }
    setHotspots((prev) => [...prev, { unit_id: uid, floor, wing, points: draftPoints }]);
    setSelected(uid);
    setDraftPoints([]);
    setDraftUnit("");
    setMode("select");
    toast.success(`Polygon for ${uid} added`);
  };

  const cancelDraft = () => { setDraftPoints([]); };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        if (mode === "draw" && draftPoints.length) cancelDraft();
        else onClose();
      } else if (e.key === "Enter" && mode === "draw" && draftPoints.length >= 3) {
        commitDraft();
      } else if ((e.key === "Delete" || e.key === "Backspace") && selected && mode === "select"
                 && document.activeElement?.tagName !== "INPUT") {
        e.preventDefault();
        handleDeleteSelected();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [mode, draftPoints, selected]);

  /* ─── Select/drag mode ─── */
  const handlePolyMouseDown = (e, unit_id) => {
    if (mode !== "select") return;
    e.stopPropagation();
    const hs = hotspots.find((h) => h.unit_id === unit_id);
    if (!hs) return;
    setSelected(unit_id);
    setDrag({
      type: "move",
      unit_id,
      startX: e.clientX,
      startY: e.clientY,
      origPoints: JSON.parse(JSON.stringify(hs.points)),
    });
  };

  const handleVertexMouseDown = (e, unit_id, idx) => {
    e.stopPropagation();
    e.preventDefault();
    setSelected(unit_id);
    const hs = hotspots.find((h) => h.unit_id === unit_id);
    if (!hs) return;
    setDrag({
      type: "vertex",
      unit_id,
      vertexIndex: idx,
      startX: e.clientX,
      startY: e.clientY,
      origPoints: JSON.parse(JSON.stringify(hs.points)),
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!drag || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const dxPct = ((e.clientX - drag.startX) / rect.width)  * 100;
    const dyPct = ((e.clientY - drag.startY) / rect.height) * 100;

    setHotspots((prev) => prev.map((h) => {
      if (h.unit_id !== drag.unit_id) return h;
      if (drag.type === "move") {
        return { ...h, points: drag.origPoints.map(([x, y]) => [
          Math.max(0, Math.min(100, x + dxPct)),
          Math.max(0, Math.min(100, y + dyPct)),
        ])};
      }
      // vertex
      const next = drag.origPoints.map((p, i) => (
        i === drag.vertexIndex
          ? [Math.max(0, Math.min(100, p[0] + dxPct)), Math.max(0, Math.min(100, p[1] + dyPct))]
          : p
      ));
      return { ...h, points: next };
    }));
  }, [drag]);

  const handleMouseUp = useCallback(() => setDrag(null), []);

  useEffect(() => {
    if (!drag) return;
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [drag, handleMouseMove, handleMouseUp]);

  const handleDeleteSelected = () => {
    if (!selected) return;
    setHotspots((prev) => prev.filter((h) => h.unit_id !== selected));
    toast.success(`Removed polygon for ${selected}`);
    setSelected(null);
  };

  const handleReset = () => {
    setHotspots(JSON.parse(JSON.stringify(original)));
    setSelected(null);
    setDraftPoints([]);
    toast.success("Reverted to last saved");
  };

  const handleSave = async () => {
    try {
      // Send all polygons; empty-points polygons will be removed server-side.
      // Also ensure removed hotspots are cleared: send them with empty points.
      const removed = original
        .filter((o) => !hotspots.find((h) => h.unit_id === o.unit_id))
        .map((o) => ({ ...o, points: [] }));
      const payload = [...hotspots, ...removed];
      await axios.put(`${API}/hotspots`, payload, { headers });
      setOriginal(JSON.parse(JSON.stringify(hotspots)));
      toast.success(`Saved ${hotspots.length} polygon${hotspots.length === 1 ? "" : "s"}`);
      onClose();
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail || err.message));
    }
  };

  const selHs = hotspots.find((h) => h.unit_id === selected);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/75 flex items-start justify-center overflow-auto p-4"
      data-testid="hotspot-editor"
    >
      <div className="w-full max-w-6xl bg-white dark:bg-[#1A1D24] border border-[#E5E5E5] dark:border-[#272A30] shadow-2xl rounded-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5] dark:border-[#272A30] flex-wrap gap-2">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#9C7C54] font-semibold">Hotspot Editor</div>
            <div className="text-sm font-medium text-[#1A1A1A] dark:text-[#EAEAEA]">
              {floor === "GF" ? "Ground Floor" : `First Floor · Block ${wing}`}
              <span className="ml-2 text-xs text-[#737373]">
                ({hotspots.length} polygon{hotspots.length === 1 ? "" : "s"})
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Mode toggle */}
            <div className="flex border border-[#E5E5E5] dark:border-[#272A30] rounded-sm overflow-hidden">
              <button
                data-testid="mode-select-btn"
                onClick={() => { setMode("select"); setDraftPoints([]); }}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs transition-colors ${
                  mode === "select" ? "bg-[#9C7C54] text-white" : "text-[#737373] hover:text-[#9C7C54]"
                }`}
              >
                <MousePointer2 size={12} /> Select
              </button>
              <button
                data-testid="mode-draw-btn"
                onClick={() => { setMode("draw"); setSelected(null); }}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs transition-colors ${
                  mode === "draw" ? "bg-emerald-600 text-white" : "text-[#737373] hover:text-emerald-600"
                }`}
              >
                <Plus size={12} /> Draw
              </button>
            </div>

            {selected && mode === "select" && (
              <button
                data-testid="delete-selected-btn"
                onClick={handleDeleteSelected}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 text-xs hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors rounded-sm"
              >
                <Trash2 size={12} /> Delete
              </button>
            )}

            <button
              onClick={handleReset}
              data-testid="reset-hotspots-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 border border-[#E5E5E5] dark:border-[#272A30] text-[#737373] text-xs hover:border-[#9C7C54] hover:text-[#9C7C54] transition-colors rounded-sm"
            >
              <RotateCcw size={12} /> Reset
            </button>
            <button
              onClick={handleSave}
              data-testid="save-hotspots-btn"
              className="flex items-center gap-1.5 px-4 py-1.5 bg-[#9C7C54] text-white text-xs tracking-wide hover:bg-[#7a5f3e] transition-colors rounded-sm"
            >
              <Save size={12} /> Save Layout
            </button>
            <button
              onClick={onClose}
              data-testid="close-editor-btn"
              className="w-8 h-8 flex items-center justify-center text-[#737373] hover:text-[#1A1A1A] dark:hover:text-[#EAEAEA]"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Instruction bar */}
        <div className="px-5 py-2 bg-amber-50 dark:bg-amber-950/20 border-b border-amber-100 dark:border-amber-900/30 flex items-center justify-between gap-2 text-xs text-amber-700 dark:text-amber-400 flex-wrap">
          {mode === "select" ? (
            <span>Click a polygon to select. Drag its body to move · drag any vertex handle to reshape · Delete key removes it.</span>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <input
                data-testid="draft-unit-input"
                type="text"
                value={draftUnit}
                onChange={(e) => setDraftUnit(e.target.value.toUpperCase())}
                placeholder="Unit ID (e.g. H-1)"
                className="px-2 py-1 text-xs bg-white dark:bg-[#0F1115] border border-amber-200 dark:border-amber-900 rounded-sm w-32 text-[#1A1A1A] dark:text-[#EAEAEA]"
              />
              <span>Click the plan to place vertices ({draftPoints.length}). Press <kbd className="px-1 border rounded">Enter</kbd> to close · <kbd className="px-1 border rounded">Esc</kbd> to cancel.</span>
              <button
                onClick={commitDraft}
                disabled={draftPoints.length < 3 || !draftUnit.trim()}
                data-testid="commit-draft-btn"
                className="flex items-center gap-1 px-2 py-1 bg-emerald-600 text-white text-[11px] rounded-sm disabled:opacity-40"
              >
                <Check size={11} /> Finish
              </button>
              {draftPoints.length > 0 && (
                <button
                  onClick={cancelDraft}
                  data-testid="cancel-draft-btn"
                  className="px-2 py-1 border border-amber-300 dark:border-amber-800 text-[11px] rounded-sm"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          {selHs && mode === "select" && (
            <span className="font-mono text-[10px] text-amber-700 dark:text-amber-400">
              {selHs.unit_id} · {selHs.points.length} pts
            </span>
          )}
        </div>

        {/* Canvas */}
        <div className="p-3 overflow-y-auto" style={{ maxHeight: "78vh" }}>
          <div className="relative select-none inline-block w-full">
            {floorPlanUrl ? (
              <img
                src={floorPlanUrl}
                alt="Floor plan"
                draggable={false}
                style={{ width: "100%", display: "block", pointerEvents: "none" }}
              />
            ) : (
              <div
                className="w-full bg-[#F5F4F2] dark:bg-[#1A1D24] flex items-center justify-center"
                style={{ minHeight: "420px" }}
              >
                <p className="text-[#737373] text-sm">No floor plan uploaded</p>
              </div>
            )}

            {/* SVG overlay */}
            <svg
              ref={svgRef}
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              onClick={handlePlanClick}
              style={{ cursor: mode === "draw" ? "crosshair" : "default" }}
              data-testid="editor-svg"
            >
              {/* Existing polygons */}
              {hotspots.map((h) => {
                const isSel = selected === h.unit_id;
                const pts   = h.points.map(([x, y]) => `${x},${y}`).join(" ");
                const cx = h.points.reduce((a, [x]) => a + x, 0) / h.points.length;
                const cy = h.points.reduce((a, [, y]) => a + y, 0) / h.points.length;
                return (
                  <g key={h.unit_id} data-testid={`editor-hs-${h.unit_id}`}>
                    <polygon
                      points={pts}
                      fill={isSel ? UNIT_COLORS.sel : UNIT_COLORS.base}
                      stroke={isSel ? UNIT_COLORS.selLn : UNIT_COLORS.baseLn}
                      strokeWidth={isSel ? 0.4 : 0.25}
                      vectorEffect="non-scaling-stroke"
                      onMouseDown={(e) => handlePolyMouseDown(e, h.unit_id)}
                      style={{ cursor: mode === "select" ? "grab" : "default" }}
                    />
                    <text
                      x={cx} y={cy}
                      textAnchor="middle" dominantBaseline="middle"
                      fontSize={1.4} fontWeight={700}
                      fill="#1A1A1A" stroke="rgba(255,255,255,0.85)" strokeWidth={0.15}
                      paintOrder="stroke"
                      style={{ pointerEvents: "none", userSelect: "none" }}
                    >
                      {h.unit_id}
                    </text>
                    {/* Vertex handles when selected */}
                    {isSel && mode === "select" && h.points.map(([x, y], idx) => (
                      <circle
                        key={idx}
                        data-vertex="1"
                        data-testid={`vertex-${h.unit_id}-${idx}`}
                        cx={x} cy={y}
                        r={0.85}
                        fill="#FFFFFF"
                        stroke={UNIT_COLORS.selLn}
                        strokeWidth={0.35}
                        vectorEffect="non-scaling-stroke"
                        onMouseDown={(e) => handleVertexMouseDown(e, h.unit_id, idx)}
                        style={{ cursor: "grab" }}
                      />
                    ))}
                  </g>
                );
              })}

              {/* Draft polygon */}
              {mode === "draw" && draftPoints.length > 0 && (
                <g data-testid="draft-poly">
                  {draftPoints.length >= 2 && (
                    <polyline
                      points={draftPoints.map(([x, y]) => `${x},${y}`).join(" ")}
                      fill={draftPoints.length >= 3 ? UNIT_COLORS.draft : "none"}
                      stroke={UNIT_COLORS.draftLn}
                      strokeWidth={0.35}
                      strokeDasharray="0.6 0.4"
                      vectorEffect="non-scaling-stroke"
                    />
                  )}
                  {draftPoints.map(([x, y], idx) => (
                    <circle
                      key={idx}
                      cx={x} cy={y} r={0.9}
                      fill={idx === 0 ? UNIT_COLORS.draftLn : "#FFFFFF"}
                      stroke={UNIT_COLORS.draftLn}
                      strokeWidth={0.35}
                      vectorEffect="non-scaling-stroke"
                    />
                  ))}
                </g>
              )}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
